/** Operadores de comparação (estilo SharePoint). */
export type FiltroOperador =
  | 'igual'
  | 'diferente'
  | 'contem'
  | 'comeca_com'
  | 'termina_com'
  | 'maior'
  | 'maior_igual'
  | 'menor'
  | 'menor_igual'
  | 'vazio'
  | 'nao_vazio';

export type FiltroConector = 'e' | 'ou';

export interface FiltroDefinidoCondicao {
  abaId: string;
  key: string;
  operador: FiltroOperador;
  valor: string;
  /** Como esta condição se combina com a anterior (ignorado na primeira). */
  conector: FiltroConector;
}

export interface FiltroDefinidoRegistros {
  mostrarTodos: boolean;
  condicoes: FiltroDefinidoCondicao[];
}

export interface OperadorFiltroOpcao {
  id: FiltroOperador;
  label: string;
}

const OPERADORES_TEXTO: OperadorFiltroOpcao[] = [
  { id: 'igual', label: 'é igual a' },
  { id: 'diferente', label: 'é diferente de' },
  { id: 'contem', label: 'contém' },
  { id: 'comeca_com', label: 'começa com' },
  { id: 'termina_com', label: 'termina com' },
  { id: 'vazio', label: 'está vazio' },
  { id: 'nao_vazio', label: 'não está vazio' },
];

const OPERADORES_DATA: OperadorFiltroOpcao[] = [
  { id: 'igual', label: 'é igual a' },
  { id: 'diferente', label: 'é diferente de' },
  { id: 'maior', label: 'é maior que' },
  { id: 'maior_igual', label: 'é maior que ou igual a' },
  { id: 'menor', label: 'é menor que' },
  { id: 'menor_igual', label: 'é menor que ou igual a' },
  { id: 'vazio', label: 'está vazio' },
  { id: 'nao_vazio', label: 'não está vazio' },
];

export function criarCondicaoFiltroVazia(): FiltroDefinidoCondicao {
  return {
    abaId: '',
    key: '',
    operador: 'igual',
    valor: '',
    conector: 'e',
  };
}

export function criarFiltroRegistrosPadrao(): FiltroDefinidoRegistros {
  return {
    mostrarTodos: true,
    condicoes: [],
  };
}

export function operadorExigeValor(operador: FiltroOperador): boolean {
  return operador !== 'vazio' && operador !== 'nao_vazio';
}

export function isCampoDataFiltro(key: string): boolean {
  const k = normalizarChaveFiltro(key);
  const tokens = [
    'data',
    'prazo',
    'invio',
    'accettazione',
    'consegna',
    'notifica',
    'giuramento',
    'remessa',
    'ajuizamento',
    'audiencia',
    'sentenca',
    'richiesto',
    'ritirato',
    'servicopago',
    'inscricao',
    'demanda',
    'limite',
    'cadastro',
  ];
  return tokens.some((token) => k.includes(token));
}

export function obterOperadoresParaCampo(key: string): OperadorFiltroOpcao[] {
  return isCampoDataFiltro(key) ? OPERADORES_DATA : OPERADORES_TEXTO;
}

export function obterLabelOperador(operador: FiltroOperador): string {
  const todos = [...OPERADORES_TEXTO, ...OPERADORES_DATA];
  const encontrado = todos.find((o) => o.id === operador);
  return encontrado?.label || operador;
}

export function normalizarChaveFiltro(key: string): string {
  return String(key || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

export function isCampoSiglaFiltro(key: string): boolean {
  return normalizarChaveFiltro(key) === 'sigla';
}

/** Divide valores empilhados na célula (ex.: "CA, DF, FINANC2"). */
export function extrairPartesValorCelula(valor: string): string[] {
  const texto = String(valor ?? '').trim();
  if (!texto || texto === '-') {
    return [];
  }
  const partes = texto
    .split(/[,;|\n\r]+/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0 && p !== '-');
  if (partes.length > 0) {
    return partes;
  }
  return [texto];
}

function normalizarTextoComparacao(valor: string): string {
  return String(valor ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function parseDataParaIso(valor: string): string | null {
  const texto = String(valor ?? '').trim();
  if (!texto || texto === '-') {
    return null;
  }
  const br = texto.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (br) {
    const [, d, m, y] = br;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  const iso = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return `${iso[1]}-${iso[2]}-${iso[3]}`;
  }
  const dt = new Date(texto);
  if (!Number.isNaN(dt.getTime())) {
    return dt.toISOString().slice(0, 10);
  }
  return null;
}

function extrairDatasIso(valorExibido: string): string[] {
  const partes = extrairPartesValorCelula(valorExibido);
  const datas: string[] = [];
  for (const parte of partes) {
    const iso = parseDataParaIso(parte);
    if (iso) {
      datas.push(iso);
    }
  }
  return datas;
}

function valorConsideradoVazio(valor: string): boolean {
  const t = String(valor ?? '').trim();
  return !t || t === '-';
}

function resolverValorFiltro(valor: string): string {
  const texto = String(valor ?? '').trim();
  if (texto.toLowerCase() === '[hoje]') {
    return new Date().toISOString().slice(0, 10);
  }
  return texto;
}

function avaliarCondicaoUnica(valorCampo: string, condicao: FiltroDefinidoCondicao): boolean {
  const operador = condicao.operador;
  const valorFiltro = resolverValorFiltro(condicao.valor);
  const campo = String(valorCampo ?? '').trim();

  if (operador === 'vazio') {
    return valorConsideradoVazio(campo);
  }
  if (operador === 'nao_vazio') {
    return !valorConsideradoVazio(campo);
  }

  if (isCampoDataFiltro(condicao.key)) {
    const datasCampo = extrairDatasIso(campo);
    const dataFiltro = parseDataParaIso(valorFiltro);
    if (!dataFiltro) {
      return false;
    }
    if (datasCampo.length === 0) {
      return false;
    }
    return datasCampo.some((dataCampo) => {
      switch (operador) {
        case 'igual':
          return dataCampo === dataFiltro;
        case 'diferente':
          return dataCampo !== dataFiltro;
        case 'maior':
          return dataCampo > dataFiltro;
        case 'maior_igual':
          return dataCampo >= dataFiltro;
        case 'menor':
          return dataCampo < dataFiltro;
        case 'menor_igual':
          return dataCampo <= dataFiltro;
        default:
          return false;
      }
    });
  }

  return avaliarOperadorTexto(extrairPartesValorCelula(campo), operador, valorFiltro);
}

function avaliarOperadorTexto(
  partes: string[],
  operador: FiltroOperador,
  valorFiltro: string
): boolean {
  const partesNorm = partes.map(normalizarTextoComparacao).filter((p) => p.length > 0);
  const filtroNorm = normalizarTextoComparacao(valorFiltro);
  const campoCompletoNorm = normalizarTextoComparacao(partes.join(', '));

  if (partesNorm.length === 0) {
    return false;
  }

  switch (operador) {
    case 'igual':
      return partesNorm.some((p) => p === filtroNorm);
    case 'diferente':
      return !partesNorm.some((p) => p === filtroNorm);
    case 'contem':
      return (
        partesNorm.some((p) => p.includes(filtroNorm)) ||
        campoCompletoNorm.includes(filtroNorm)
      );
    case 'comeca_com':
      return partesNorm.some((p) => p.startsWith(filtroNorm));
    case 'termina_com':
      return partesNorm.some((p) => p.endsWith(filtroNorm));
    default:
      return false;
  }
}

/** Avalia a lista de condições com conectores E/Ou (da esquerda para a direita). */
export function avaliarCondicoesFiltroDefinido(
  condicoes: FiltroDefinidoCondicao[],
  obterValorCampo: (abaId: string, key: string) => string
): boolean {
  const validas = (condicoes || []).filter((c) => c.abaId && c.key && c.operador);
  if (validas.length === 0) {
    return true;
  }

  let resultado = avaliarCondicaoUnica(
    obterValorCampo(validas[0].abaId, validas[0].key),
    validas[0]
  );

  for (let i = 1; i < validas.length; i++) {
    const cond = validas[i];
    const parcial = avaliarCondicaoUnica(obterValorCampo(cond.abaId, cond.key), cond);
    if (cond.conector === 'ou') {
      resultado = resultado || parcial;
    } else {
      resultado = resultado && parcial;
    }
  }

  return resultado;
}
